import { Link } from "react-router-dom";
import { AFRICA_COUNTRIES, LIFESTYLE } from "@/data/products";
import { Check, ArrowRight } from "lucide-react";
import WorldMap from "@/components/WorldMap";
import { useTranslation } from "react-i18next";

const CHANNELS = [
  "Distributors", "Supermarkets", "Hotels", "Restaurants", "Convenience Stores",
  "Government Supply", "NGOs", "Schools", "Hospitals", "Airlines", "Shipping",
];

export default function ExportAfrica() {
  const { t } = useTranslation();
  return (
    <div data-testid="export-africa-page">
      <section className="relative bg-neutral-950 text-white pt-20 pb-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={LIFESTYLE.africa_market} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/50 to-neutral-950" />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="eyebrow text-gold-400 mb-4">{t("export.eyebrow")}</div>
          <h1 className="font-display font-bold text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
            {t("export.title_1")} <span className="gold-gradient-text">{t("export.title_2")}</span>
          </h1>
          <p className="mt-6 text-lg text-neutral-300 max-w-2xl leading-relaxed">{t("export.lead")}</p>
        </div>
      </section>

      {/* Interactive World Map */}
      <section className="py-24 bg-background" data-testid="export-map-interactive">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="eyebrow">{t("export.map_title")}</div>
            <h2 className="font-display font-bold text-4xl tracking-tighter mt-2">{t("export.map_subtitle")}</h2>
          </div>
          <div className="rounded-3xl border border-border bg-muted/20 p-4 md:p-8">
            <WorldMap />
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <div className="eyebrow">Channels We Serve</div>
            <h2 className="font-display font-bold text-4xl tracking-tighter">Every route to market. One reliable partner.</h2>
            <p className="text-muted-foreground leading-relaxed">
              From formal retail to institutional supply, we structure programmes with the packaging, pricing and logistics that fit each channel.
            </p>
            <img src={LIFESTYLE.supermarket} alt="Retail" className="rounded-2xl aspect-video object-cover w-full" />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {CHANNELS.map((c) => (
              <div key={c} className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-gold-400 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gold-400/15 flex items-center justify-center">
                  <Check className="w-4 h-4 text-gold-500" />
                </div>
                <span className="font-medium text-sm">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-muted/40">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-10">
            <div className="eyebrow">Target Markets</div>
            <h2 className="font-display font-bold text-4xl tracking-tighter mt-2">15 countries. Growing every quarter.</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {AFRICA_COUNTRIES.map((c) => (
              <div key={c} className="p-4 rounded-xl bg-background border border-border font-display font-semibold text-center hover:border-gold-400 hover:text-gold-500 transition-colors">
                {c}
              </div>
            ))}
          </div>
          <div className="mt-8 text-sm text-muted-foreground text-center">+ future expansion across Africa, Middle East and the Americas.</div>
        </div>
      </section>

      <section className="py-24 bg-neutral-950 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="eyebrow text-gold-400">Ready to grow with SBC?</div>
          <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tighter mt-4">Apply for a distributor partnership today.</h2>
          <p className="text-neutral-300 mt-6 max-w-2xl mx-auto">Our export team reviews every application within 5 business days.</p>
          <Link to="/distributor" className="inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-300 text-neutral-950 rounded-full px-8 py-4 text-sm font-semibold mt-8 transition-colors">
            Become a Distributor <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
